import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getBotDetail,
  getBotKnowledge,
  getBotLifecycle,
  adminTestBot,
  adminEditReview,
  renameBot,
  suspendProvider,
  reinstateProvider,
  BotDetail,
  KnowledgeFile,
  LifecycleEvent,
} from '../../services/providerReviewService';
import Markdown from './Markdown';
import OptimizerRunsSection from './OptimizerRunsSection';
import VoiceDevicesSection from './VoiceDevicesSection';
import { statusKind, REJECTION_REASONS } from './botStatus';
import { useImeSafeEnter } from '../../hooks/useImeSafeEnter';

interface Msg { from: 'me' | 'bot'; text: string }

/** Category-specific probe questions so a reviewer can quickly test guardrails. */
const PROBES: Record<string, string[]> = {
  general: ['probe.intro', 'probe.scope', 'probe.unknown'],
  financial: ['probe.intro', 'probe.finAdvice', 'probe.unknown'],
  health: ['probe.intro', 'probe.diagnosis', 'probe.unknown'],
};

/**
 * Comprehensive review detail for one bot: runtime configuration (persona,
 * assembled prompt, model/tools, disclaimer), knowledge with moderation
 * verdicts, provider identity, the full status timeline (who/when/why), and a
 * live chat preview with guardrail probes — everything an admin needs to
 * approve or reject without leaving the row.
 */
const BotDetailPanel: React.FC<{ botId: string }> = ({ botId }) => {
  const { t } = useTranslation();
  const [detail, setDetail] = useState<BotDetail | null>(null);
  const [files, setFiles] = useState<KnowledgeFile[]>([]);
  const [timeline, setTimeline] = useState<LifecycleEvent[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [editActing, setEditActing] = useState(false);
  // Rename (2026-08-11): the only surface that can rename a platform-managed
  // bot — the provider console refuses those by design.
  const [nameDraft, setNameDraft] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [renameMsg, setRenameMsg] = useState<string | null>(null);
  const [editResult, setEditResult] = useState<string | null>(null);
  const [rejectReasons, setRejectReasons] = useState<string[]>([]);
  const [rejectNote, setRejectNote] = useState('');
  const [rejecting, setRejecting] = useState(false);
  // Provider-account suspension (2026-07-09): confirm step + note + result.
  const [suspending, setSuspending] = useState(false);
  const [suspendNote, setSuspendNote] = useState('');
  const [suspendActing, setSuspendActing] = useState(false);
  const [suspendResult, setSuspendResult] = useState<string | null>(null);
  const msgsRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [d, f, l] = await Promise.all([
        getBotDetail(botId),
        getBotKnowledge(botId),
        getBotLifecycle(botId).catch(() => [] as LifecycleEvent[]),
      ]);
      setDetail(d);
      setFiles(f);
      setTimeline(l);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [botId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    msgsRef.current?.scrollTo({ top: msgsRef.current.scrollHeight, behavior: 'smooth' });
  }, [msgs, sending]);

  const send = async (textArg?: string) => {
    const text = (textArg ?? input).trim();
    if (!text || sending) return;
    setInput('');
    setMsgs((m) => [...m, { from: 'me', text }]);
    setSending(true);
    try {
      const answer = await adminTestBot(botId, text);
      setMsgs((m) => [...m, { from: 'bot', text: answer }]);
    } catch (e) {
      setMsgs((m) => [...m, { from: 'bot', text: e instanceof Error ? e.message : String(e) }]);
    } finally {
      setSending(false);
    }
  };
  const imeEnter = useImeSafeEnter<HTMLInputElement>(() => send());

  const editActionBar = (pos: 'top' | 'bottom') => (
    <div className="edit-action-bar" key={pos}>
      <button
        className="btn-lg approve"
        disabled={editActing}
        onClick={async () => {
          setEditActing(true);
          try { await adminEditReview(botId, true); setEditResult(t('review.detail.editApproved', 'Edit approved — now live.')); await load(); }
          catch (e) { setEditResult(e instanceof Error ? e.message : String(e)); }
          finally { setEditActing(false); }
        }}
      >
        ✓ {t('review.detail.approveEdit', 'Approve edit')}
      </button>
      <button className="btn-lg reject secondary danger-text" disabled={editActing} onClick={() => setRejecting((v) => !v)}>
        ✕ {t('review.detail.rejectEdit', 'Reject edit')}
      </button>
    </div>
  );

  if (error) return <div className="review-error">{error}</div>;
  if (!detail) return <div className="detail-loading">{t('review.detail.loading', 'Loading…')}</div>;

  const currentName = detail.bot.displayName || '';
  const nameValue = nameDraft || currentName;

  const cat = detail.bot.disclaimerCategory || 'general';
  const probes = PROBES[cat] || PROBES.general;
  const fmt = (iso?: string) => (iso ? new Date(iso).toLocaleString() : '—');

  return (
    <div className="bot-detail">
      {/* Rename: Bots.displayName + the public ExpertProfiles.title together.
          Nothing could change either after creation until 2026-08-11. */}
      <section className="detail-block">
        <h4>{t('review.detail.nameTitle', 'Display name')}</h4>
        <div className="rename-row" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            value={nameValue}
            maxLength={80}
            onChange={(e) => setNameDraft(e.target.value)}
            style={{ flex: '1 1 260px' }}
            aria-label={t('review.detail.nameTitle', 'Display name')}
          />
          <button
            disabled={renaming || !nameValue.trim() || nameValue.trim() === currentName}
            onClick={async () => {
              setRenaming(true); setRenameMsg(null);
              try {
                await renameBot(botId, nameValue.trim());
                setRenameMsg(t('review.detail.nameSaved', 'Renamed — the console and the public page both show the new name.'));
                setNameDraft('');
                await load();
              } catch (e) { setRenameMsg(String(e)); }
              setRenaming(false);
            }}
          >
            {renaming ? t('review.detail.nameSaving', 'Saving…') : t('review.detail.nameSave', 'Rename')}
          </button>
        </div>
        {renameMsg && <div className="hint">{renameMsg}</div>}
      </section>

      {/* Config-at-a-glance chips */}
      <div className="detail-chips">
        <span className="chip">{t(`botType.${detail.bot.botType}`, detail.bot.botType)}</span>
        <span className="chip">{t(`domain.${cat}`, cat)}</span>
        {detail.bot.entryChannel && <span className="chip mono">{detail.bot.entryChannel}</span>}
        {detail.config?.model && <span className="chip mono">{detail.config.model}</span>}
        {(detail.config?.tools || []).map((tool) => (
          <span key={tool} className="chip mono">{tool}</span>
        ))}
        {detail.profile && <span className="chip mono">/{detail.profile.slug}</span>}
        <span className="chip muted">{t('review.detail.created', 'Created')}: {fmt(detail.bot.createdAt)}</span>
        {detail.bot.submittedAt && (
          <span className="chip muted">{t('review.detail.submitted', 'Submitted')}: {fmt(detail.bot.submittedAt)}</span>
        )}
      </div>

      {/* Item 1: pending edit of a LIVE bot — quick approve/reject of just the
          new strings. The action bar renders at BOTH the top and bottom of the
          section (user feedback: buttons must be prominent and reachable
          without scrolling past a long prompt). */}
      {detail.pendingInstructions && (
        <section className="detail-block pending-edit">
          <h4>{t('review.detail.pendingEdit', 'Pending edit (live bot keeps serving the approved text until you decide)')}</h4>

          {editActionBar('top')}

          {typeof detail.pendingWebSearch === 'boolean' && detail.pendingWebSearch !== !!detail.webSearch && (
            <div className="detail-chips">
              <span className="chip">{detail.pendingWebSearch
                ? t('review.detail.pendingWebSearchOn', 'Pending: web search ON')
                : t('review.detail.pendingWebSearchOff', 'Pending: web search OFF')}</span>
            </div>
          )}
          <pre className="detail-instructions persona">{detail.pendingPersona || detail.pendingInstructions}</pre>
          <details className="detail-collapse">
            <summary>{t('review.detail.pendingFull', 'Full new system prompt')}</summary>
            <pre className="detail-instructions">{detail.pendingInstructions}</pre>
          </details>

          {editActionBar('bottom')}

          {rejecting && (
            <div className="reject-reasons-panel">
              <div className="reject-reasons-title">{t('review.reasonsTitle', 'Rejection reasons (select at least one — shared with the provider)')}</div>
              <div className="reject-reasons-list">
                {REJECTION_REASONS.map((code) => (
                  <label key={code} className="reject-reason-item">
                    <input
                      type="checkbox"
                      checked={rejectReasons.includes(code)}
                      onChange={() => setRejectReasons((r) => (r.includes(code) ? r.filter((x) => x !== code) : [...r, code]))}
                    />
                    {t(`reasons.${code}`, code)}
                  </label>
                ))}
              </div>
              {/* Free-text note — BotTable's reject flow always had this; here it
                  was silently dropped (hardcoded undefined) until 2026-08-29. */}
              <textarea
                value={rejectNote}
                onChange={(e) => setRejectNote(e.target.value)}
                placeholder={t('review.notePlaceholder', 'Note (optional, shown to provider)') as string}
                maxLength={1000}
              />
              <button
                className="danger btn-lg"
                disabled={editActing || rejectReasons.length === 0 || (rejectReasons.includes('other') && !rejectNote.trim())}
                title={
                  rejectReasons.length === 0
                    ? (t('review.reasonsRequired', 'Select at least one reason') as string)
                    : rejectReasons.includes('other') && !rejectNote.trim()
                      ? (t('review.noteRequiredOther', 'Add a note when “Other” is selected') as string)
                      : undefined
                }
                onClick={async () => {
                  setEditActing(true);
                  try { await adminEditReview(botId, false, rejectNote.trim() || undefined, rejectReasons); setEditResult(t('review.detail.editRejected', 'Edit rejected — discarded.')); setRejecting(false); setRejectNote(''); await load(); }
                  catch (e) { setEditResult(e instanceof Error ? e.message : String(e)); }
                  finally { setEditActing(false); }
                }}
              >
                {t('review.detail.confirmReject', 'Confirm rejection')}
              </button>
            </div>
          )}
          {editResult && <p className="detail-muted">{editResult}</p>}
        </section>
      )}

      <div className="detail-grid">
        <section className="detail-block">
          <h4>{t('review.detail.persona', 'Persona (provider input)')}</h4>
          {detail.persona ? (
            <pre className="detail-instructions persona">{detail.persona}</pre>
          ) : (
            <p className="detail-muted">{t('review.detail.noPersona', 'No persona set.')}</p>
          )}
          <details className="detail-collapse">
            <summary>{t('review.detail.fullPrompt', 'Full system prompt (as assembled)')}</summary>
            <pre className="detail-instructions">{detail.instructions || t('review.detail.none', '(none)')}</pre>
          </details>
          {detail.profile?.bio && (
            <>
              <h4>{t('review.detail.publicBio', 'Public profile bio')}</h4>
              <p className="detail-bio">{detail.profile.bio}</p>
            </>
          )}
        </section>

        <OptimizerRunsSection botId={botId} />

        <VoiceDevicesSection key={botId} botId={botId} initial={detail.voice} />

        <section className="detail-block">
          <h4>{t('review.detail.knowledge', 'Knowledge files')} ({files.length})</h4>
          {files.length === 0 ? (
            <p className="detail-muted">{t('review.detail.noKnowledge', 'No knowledge files.')}</p>
          ) : (
            <ul className="detail-files">
              {files.map((f) => (
                <li key={f.fileId}>
                  <span className="file-name">{f.fileName}</span>
                  {f.label && <span className="file-label">{f.label}</span>}
                  {f.description && <div className="detail-muted file-desc">{f.description}</div>}
                  <span className="file-meta">
                    {f.moderation && (
                      <span className={'mod-pill ' + (f.moderation === 'clean' ? 'ok' : 'warn')}>
                        {f.moderation === 'clean' ? t('review.detail.modClean', 'screened') : f.moderation}
                      </span>
                    )}
                    {f.ingestStatus && <span className="file-status">{f.ingestStatus}</span>}
                  </span>
                </li>
              ))}
            </ul>
          )}

          <h4>{t('review.detail.provider', 'Provider')}</h4>
          <div className="detail-provider">
            <div>{detail.provider.name || detail.provider.email || detail.provider.providerId}{detail.provider.name && detail.provider.email ? <span className="detail-muted"> · {detail.provider.email}</span> : null}</div>
            <span className={'verify-pill ' + (detail.provider.verificationStatus === 'verified' ? 'ok' : 'no')}>
              {detail.provider.verificationStatus === 'verified'
                ? t('review.detail.verified', 'verified')
                : t('review.detail.unverified', 'unverified')}
            </span>
            {detail.provider.accessEnabled === false && (
              <span className="verify-pill no">{t('review.detail.providerSuspended', 'account suspended')}</span>
            )}
          </div>

          {/* Provider ACCOUNT suspension — blocks the whole console/API for
              this provider (distinct from per-bot Suspend) and emails them. */}
          <div className="provider-suspend">
            {detail.provider.accessEnabled === false ? (
              <>
                {detail.provider.suspendNote && (
                  <div className="detail-muted">{t('review.detail.suspendNoteLabel', 'Suspension note')}: {detail.provider.suspendNote}</div>
                )}
                <button
                  className="secondary"
                  disabled={suspendActing}
                  onClick={async () => {
                    setSuspendActing(true);
                    try {
                      const r = await reinstateProvider(detail.provider.providerId);
                      setSuspendResult(t('review.detail.reinstated', 'Provider reinstated.') + (r.emailSent ? '' : ` ${t('review.detail.emailNotSent', '(notification email not sent)')}`));
                      await load();
                    } catch (e) { setSuspendResult(e instanceof Error ? e.message : String(e)); }
                    finally { setSuspendActing(false); }
                  }}
                >
                  {t('review.detail.reinstateProvider', 'Reinstate provider account')}
                </button>
              </>
            ) : !suspending ? (
              <button className="secondary danger-text" onClick={() => setSuspending(true)}>
                {t('review.detail.suspendProvider', 'Suspend provider account')}
              </button>
            ) : (
              <div className="reject-reasons-panel">
                <div className="reject-reasons-title">
                  {t('review.detail.suspendConfirm', 'Suspend this provider? They lose access to the console and API for ALL their bots, and are notified by email.')}
                </div>
                <textarea
                  value={suspendNote}
                  onChange={(e) => setSuspendNote(e.target.value)}
                  placeholder={t('review.detail.suspendNotePh', 'Reason (included in the email to the provider)') as string}
                  maxLength={1000}
                />
                <div className="edit-action-bar">
                  <button
                    className="danger btn-lg"
                    disabled={suspendActing}
                    onClick={async () => {
                      setSuspendActing(true);
                      try {
                        const r = await suspendProvider(detail.provider.providerId, suspendNote.trim() || undefined);
                        setSuspendResult(t('review.detail.suspended', 'Provider suspended.') + (r.emailSent ? ` ${t('review.detail.emailSent', 'Notification email sent.')}` : ` ${t('review.detail.emailNotSent', '(notification email not sent)')}`));
                        setSuspending(false);
                        await load();
                      } catch (e) { setSuspendResult(e instanceof Error ? e.message : String(e)); }
                      finally { setSuspendActing(false); }
                    }}
                  >
                    {t('review.detail.confirmSuspend', 'Confirm suspension')}
                  </button>
                  <button className="secondary" disabled={suspendActing} onClick={() => setSuspending(false)}>
                    {t('review.detail.cancel', 'Cancel')}
                  </button>
                </div>
              </div>
            )}
            {suspendResult && <p className="detail-muted">{suspendResult}</p>}
          </div>

          <h4>{t('review.detail.timeline', 'Review timeline')}</h4>
          {timeline.length === 0 ? (
            <p className="detail-muted">{t('review.detail.noTimeline', 'No status changes yet.')}</p>
          ) : (
            <ul className="detail-timeline">
              {timeline.map((e, i) => (
                <li key={i}>
                  <span className={'status-pill sm ' + statusKind(e.to || '')}>{t(`status.${e.to || ''}`, e.to || '')}</span>
                  <span className="tl-meta">
                    {fmt(e.eventTs)}
                    {e.by ? ` · ${e.by}` : ''}
                  </span>
                  {e.reasons && e.reasons.length > 0 && (
                    <div className="tl-note">{e.reasons.map((c) => t(`reasons.${c}`, c)).join(' / ')}</div>
                  )}
                  {e.note && <div className="tl-note">{e.note}</div>}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <section className="detail-block detail-preview">
        <h4>{t('review.detail.preview', 'Try this bot')}</h4>
        <div className="preview-probes">
          {probes.map((key) => (
            <button key={key} className="probe-chip" disabled={sending} onClick={() => send(t(`review.${key}`))}>
              {t(`review.${key}`)}
            </button>
          ))}
        </div>
        <div className="preview-msgs" ref={msgsRef}>
          {msgs.length === 0 && !sending && (
            <div className="detail-muted">{t('review.detail.previewHint', 'Send a message or tap a suggestion to try this bot.')}</div>
          )}
          {msgs.map((m, i) => (
            <div key={i} className={'preview-bubble ' + m.from}>
              {m.from === 'bot' ? <Markdown text={m.text} /> : m.text}
            </div>
          ))}
          {sending && (
            <div className="preview-bubble bot loading">
              <span className="dot" /><span className="dot" /><span className="dot" />
            </div>
          )}
        </div>
        <div className="preview-input">
          <input
            value={input}
            placeholder={t('review.detail.previewPlaceholder', 'Type a message…') as string}
            onChange={(e) => setInput(e.target.value)}
            {...imeEnter.props}
            disabled={sending}
          />
          <button onClick={() => send()} disabled={sending || !input.trim()}>
            {t('review.detail.send', 'Send')}
          </button>
        </div>
      </section>
    </div>
  );
};

export default BotDetailPanel;
